import React from "react";
import { FaTimes, FaSave, FaBuilding, FaCalendarAlt } from "react-icons/fa";
import ComboBox from "./ComboBox";

function ProductModal({
  open,
  onClose,
  onSave,
  isSaving,
  editId,
  form,
  setForm,
  companyList,
  shortNameList,
  catalogueList,
  qualityList,
  productNameList,
  handleCompanyChange,
}) {
  const today = new Date().toISOString().slice(0, 10);
  if (!open) return null;

  const productOptions = (productNameList || []).map((p) =>
    typeof p === "string" ? p : p.productName
  );

  return (
    <div
      className="pm-overlay"
      onClick={(e) => e.target === e.currentTarget && !isSaving && onClose()}
      role="dialog"
      aria-modal="true"
    >
      <div className="pm-modal">
        {/* HEADER */}
        <div className="pm-header">
          <h2 className="pm-title">{editId ? "Edit Product" : "Add Product"}</h2>
          <button className="pm-closeBtn" onClick={onClose} disabled={isSaving} type="button" aria-label="Close">
            <FaTimes />
          </button>
        </div>

        {/* BODY (scrollable) */}
        <div className="pm-body">
          <div className="pm-grid">
            <ComboBox
              label="Product Name"
              value={form.productName}
              onChange={(val) => setForm({ ...form, productName: val })}
              options={productOptions}
              placeholder="Select product"
            />

            <ComboBox
              label="Company Name"
              value={form.companyName}
              onChange={handleCompanyChange}
              options={companyList}
              icon={FaBuilding}
              placeholder="Select company"
            />

            <ComboBox
              label="Short Name"
              value={form.shortName}
              onChange={(val) => setForm({ ...form, shortName: val })}
              options={shortNameList}
            />

            <ComboBox
              label="Catalogue"
              value={form.catalogueName}
              onChange={(val) => setForm({ ...form, catalogueName: val })}
              options={catalogueList}
            />

            <ComboBox
              label="Quality"
              value={form.qualityName}
              onChange={(val) => setForm({ ...form, qualityName: val })}
              options={qualityList}
            />

            <div className="pm-field">
              <label>Design No</label>
              <input
                value={form.designNumber}
                onChange={(e) => setForm({ ...form, designNumber: e.target.value })}
              />
            </div>

            {/* ✅ DATE (future date blocked) */}
            <div className="pm-field">
              <label>Date</label>
              <div className="pm-dateWrap">
                <FaCalendarAlt className="pm-dateIcon" />
                <input
                  type="date"
                  value={form.date || ""}
                  max={today}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                />
              </div>
            </div>

            <div className="pm-field">
              <label>W Rate</label>
              <input
                type="number"
                value={form.wRate}
                onChange={(e) => setForm({ ...form, wRate: e.target.value })}
              />
            </div>

            <div className="pm-field">
              <label>X Rate</label>
              <input
                type="number"
                value={form.xRate}
                onChange={(e) => setForm({ ...form, xRate: e.target.value })}
              />
            </div>
          </div>
        </div>

        {/* FOOTER (sticky feel) */}
        <div className="pm-footer">
          <button className="pm-btn pm-save" onClick={onSave} disabled={isSaving} type="button">
            <FaSave /> {isSaving ? "Saving..." : editId ? "Update" : "Save"}
          </button>

          <button className="pm-btn pm-cancel" onClick={onClose} disabled={isSaving} type="button">
            Cancel
          </button>
        </div>
      </div>

      {/* ✅ FULL RESPONSIVE CSS (fixes: overflow, height, scroll, grid breakpoints) */}
      <style>{`
        .pm-overlay * { box-sizing: border-box; }

        .pm-overlay{
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.55);
          backdrop-filter: blur(6px);
          z-index: 1000;

          /* key for responsiveness */
          overflow: auto;                 /* ✅ overlay scroll if needed */
          -webkit-overflow-scrolling: touch;

          display: flex;
          align-items: center;
          justify-content: center;
          padding: 12px;
        }

        /* On short screens, keep modal top-friendly */
        @media (max-height: 700px){
          .pm-overlay{
            align-items: flex-start;
            padding-top: 14px;
            padding-bottom: 14px;
          }
        }

        .pm-modal{
          width: 100%;
          max-width: 860px;               /* ✅ little wider on desktop */
          background: #fff;
          border-radius: 16px;
          box-shadow: 0 20px 55px rgba(0,0,0,0.25);
          overflow: hidden;

          /* ✅ important: keep inside viewport */
          max-height: calc(100vh - 24px);
          display: flex;
          flex-direction: column;

          animation: pmIn 0.18s ease;
        }

        @keyframes pmIn{
          from{ transform: translateY(10px); opacity:0; }
          to{ transform: translateY(0); opacity:1; }
        }

        .pm-header{
          padding: 12px 14px;
          color: #fff;
          background: linear-gradient(135deg, #6366f1, #8b5cf6);
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
          flex: 0 0 auto;
        }

        .pm-title{
          margin: 0;
          font-size: clamp(16px, 3.8vw, 20px);
          font-weight: 900;
          line-height: 1.2;
        }

        .pm-closeBtn{
          border: 0;
          cursor: pointer;
          color: #fff;
          background: rgba(255,255,255,0.18);
          width: 38px;
          height: 38px;
          border-radius: 10px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }

        .pm-body{
          padding: 14px;
          overflow: auto;                 /* ✅ scroll content */
          -webkit-overflow-scrolling: touch;
          flex: 1 1 auto;
          background: #fff;
        }

        .pm-grid{
          display: grid;
          grid-template-columns: 1fr;
          gap: 12px;
        }

        /* ✅ Tablet: 2 columns */
        @media (min-width: 640px){
          .pm-grid{ grid-template-columns: 1fr 1fr; }
        }

        /* ✅ Laptop/Desktop: 3 columns for better use of space */
        @media (min-width: 1100px){
          .pm-grid{ grid-template-columns: 1fr 1fr 1fr; }
        }

        /* ✅ Prevent overflow from ComboBox (very important) */
        .pm-grid > *{ min-width: 0; }

        .pm-field{
          display: flex;
          flex-direction: column;
          gap: 6px;
          min-width: 0;
        }

        .pm-field label{
          font-size: 12px;
          font-weight: 800;
          color: #334155;
        }

        .pm-field input{
          width: 100%;
          height: 42px;
          padding: 0 12px;
          border-radius: 12px;
          border: 1px solid #e5e7eb;
          font-size: 14px;
          outline: none;
          background: #fff;
        }

        .pm-field input:focus{
          border-color: #6366f1;
          box-shadow: 0 0 0 4px rgba(99,102,241,.14);
        }

        .pm-dateWrap{ position: relative; }
        .pm-dateIcon{
          position: absolute;
          left: 12px;
          top: 50%;
          transform: translateY(-50%);
          color: #6b7280;
          font-size: 14px;
          pointer-events: none;
        }
        .pm-dateWrap input{ padding-left: 34px; }

        .pm-footer{
          flex: 0 0 auto;
          padding: 12px 14px;
          display: flex;
          gap: 10px;
          justify-content: flex-end;
          flex-wrap: wrap;
          background: #f8fafc;
          border-top: 1px solid #e5e7eb;
        }

        .pm-btn{
          height: 42px;
          padding: 0 14px;
          border-radius: 12px;
          border: 0;
          font-weight: 900;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }

        .pm-save{
          background: linear-gradient(135deg, #6366f1, #8b5cf6);
          color: #fff;
        }

        .pm-cancel{
          background: #e5e7eb;
          color: #0f172a;
        }

        /* ✅ Mobile buttons full width */
        @media (max-width: 480px){
          .pm-footer{
            flex-direction: column;
            align-items: stretch;
          }
          .pm-btn{ width: 100%; }
        }
      `}</style>
    </div>
  );
}

export default ProductModal;







// import React from "react";
// import { FaTimes, FaSave, FaBuilding } from "react-icons/fa";
// import ComboBox from "./ComboBox";

// function ProductModal({
//   open,
//   onClose,
//   onSave,
//   isSaving,
//   editId,
//   form,
//   setForm,
//   companyList,
//   shortNameList,
//   catalogueList,
//   qualityList,
//   productNameList,
//   handleCompanyChange,
// }) {
//   if (!open) return null;

//   const productOptions = (productNameList || []).map((p) =>
//     typeof p === "string" ? p : p.productName
//   );

//   return (
//     <div
//       className="pm-overlay"
//       onClick={(e) =>
//         e.target === e.currentTarget && !isSaving && onClose()
//       }
//     >
//       <div className="pm-modal">

//         {/* HEADER */}
//         <div className="pm-header">
//           <h2>{editId ? "Edit Product" : "Add Product"}</h2>
//           <button onClick={onClose} disabled={isSaving}>
//             <FaTimes />
//           </button>
//         </div>

//         {/* BODY */}
//         <div className="pm-body">
//           <div className="pm-grid">

//             <ComboBox
//               label="Product Name"
//               value={form.productName}
//               onChange={(val) =>
//                 setForm({ ...form, productName: val })
//               }
//               options={productOptions}
//               placeholder="Select product"
//             />

//             <ComboBox
//               label="Company Name"
//               value={form.companyName}
//               onChange={handleCompanyChange}
//               options={companyList}
//               icon={FaBuilding}
//               placeholder="Select company"
//             />

//             <ComboBox
//               label="Short Name"
//               value={form.shortName}
//               onChange={(val) =>
//                 setForm({ ...form, shortName: val })
//               }
//               options={shortNameList}
//             />

//             <ComboBox
//               label="Catalogue"
//               value={form.catalogueName}
//               onChange={(val) =>
//                 setForm({ ...form, catalogueName: val })
//               }
//               options={catalogueList}
//             />

//             <ComboBox
//               label="Quality"
//               value={form.qualityName}
//               onChange={(val) =>
//                 setForm({ ...form, qualityName: val })
//               }
//               options={qualityList}
//             />

//             <div className="pm-field">
//               <label>Design No</label>
//               <input
//                 value={form.designNumber}
//                 onChange={(e) =>
//                   setForm({
//                     ...form,
//                     designNumber: e.target.value,
//                   })
//                 }
//               />
//             </div>

//             <div className="pm-field">
//               <label>W Rate</label>
//               <input
//                 type="number"
//                 value={form.wRate}
//                 onChange={(e) =>
//                   setForm({ ...form, wRate: e.target.value })
//                 }
//               />
//             </div>

//             <div className="pm-field">
//               <label>X Rate</label>
//               <input
//                 type="number"
//                 value={form.xRate}
//                 onChange={(e) =>
//                   setForm({ ...form, xRate: e.target.value })
//                 }
//               />
//             </div>

//           </div>
//         </div>

//         {/* FOOTER */}
//         <div className="pm-footer">
//           <button
//             className="pm-btn pm-save"
//             onClick={onSave}
//             disabled={isSaving}
//           >
//             <FaSave /> {isSaving ? "Saving..." : "Save"}
//           </button>

//           <button className="pm-btn pm-cancel" onClick={onClose}>
//             Cancel
//           </button>
//         </div>
//       </div>

//       {/* ✅ FULL RESPONSIVE CSS */}
//       <style>{`
//         .pm-overlay {
//           position: fixed;
//           inset: 0;
//           background: rgba(0,0,0,0.6);
//           backdrop-filter: blur(8px);
//           display: flex;
//           align-items: center;
//           justify-content: center;
//           padding: 10px;
//           z-index: 1000;
//         }

//         .pm-modal {
//           width: 100%;
//           max-width: 750px;
//           background: #ffffff;
//           border-radius: 16px;
//           overflow: hidden;
//           box-shadow: 0 20px 50px rgba(0,0,0,0.25);
//           animation: fadeIn 0.25s ease;
//         }

//         @keyframes fadeIn {
//           from { transform: translateY(20px); opacity: 0; }
//           to { transform: translateY(0); opacity: 1; }
//         }

//         .pm-header {
//           background: linear-gradient(135deg, #6366f1, #8b5cf6);
//           color: white;
//           padding: 14px 18px;
//           display: flex;
//           justify-content: space-between;
//           align-items: center;
//         }

//         .pm-header h2 {
//           font-size: clamp(16px, 4vw, 20px);
//         }

//         .pm-header button {
//           background: rgba(255,255,255,0.2);
//           border: none;
//           border-radius: 6px;
//           color: white;
//           padding: 6px;
//         }

//         .pm-body {
//           padding: clamp(14px, 3vw, 22px);
//         }

//         .pm-grid {
//           display: grid;
//           grid-template-columns: 1fr;
//           gap: 12px;
//         }

//         /* Tablet */
//         @media (min-width: 600px) {
//           .pm-grid {
//             grid-template-columns: 1fr 1fr;
//           }
//         }

//         /* Desktop */
//         @media (min-width: 1024px) {
//           .pm-grid {
//             grid-template-columns: 1fr 1fr;
//           }
//         }

//         .pm-field label {
//           font-size: 12px;
//           font-weight: 600;
//         }

//         .pm-field input {
//           width: 100%;
//           padding: 10px;
//           border-radius: 8px;
//           border: 1px solid #ddd;
//           margin-top: 4px;
//           font-size: 14px;
//         }

//         .pm-footer {
//           padding: 12px 16px;
//           display: flex;
//           gap: 10px;
//           justify-content: flex-end;
//           background: #f9fafb;
//           flex-wrap: wrap;
//         }

//         /* Mobile buttons full width */
//         @media (max-width: 480px) {
//           .pm-footer {
//             flex-direction: column;
//           }

//           .pm-btn {
//             width: 100%;
//           }
//         }

//         .pm-btn {
//           padding: 10px 16px;
//           border-radius: 8px;
//           border: none;
//           font-weight: 600;
//           cursor: pointer;
//           display: flex;
//           align-items: center;
//           justify-content: center;
//           gap: 6px;
//         }

//         .pm-save {
//           background: linear-gradient(135deg, #6366f1, #8b5cf6);
//           color: white;
//         }

//         .pm-cancel {
//           background: #e5e7eb;
//         }
//       `}</style>
//     </div>
//   );
// }

// export default ProductModal;